import { lazy, ComponentType } from 'react';

const RELOAD_FLAG = 'lazyRetry:reloaded';

function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes('Failed to fetch') ||
      error.message.includes('dynamically imported module') ||
      error.message.includes('Importing a module script failed'))
  );
}

/**
 * Wraps React.lazy with retry logic to handle "Failed to fetch dynamically imported module" errors.
 * This commonly happens during Vite HMR updates or network timing issues.
 *
 * After a deploy, old chunk hashes are gone from the server permanently — retrying won't recover
 * them. In that case we force a one-time full reload so the browser fetches the new index.html
 * with up-to-date hash references. A sessionStorage flag prevents an infinite reload loop if the
 * failure turns out to be something else.
 */
export function lazyRetry<T extends ComponentType<unknown>>(
  componentImport: () => Promise<{ default: T }>,
  retries = 3,
  interval = 1000
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | undefined;

    for (let i = 0; i < retries; i++) {
      try {
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, interval));
        }
        const mod = await componentImport();
        try {
          sessionStorage.removeItem(RELOAD_FLAG);
        } catch {
          // sessionStorage may be unavailable (private mode, sandboxed iframe) — ignore
        }
        return mod;
      } catch (error) {
        lastError = error as Error;
        console.warn(`Lazy import failed (attempt ${i + 1}/${retries}):`, error);

        if (i < retries - 1 && isChunkLoadError(error)) {
          continue;
        }
      }
    }

    if (isChunkLoadError(lastError)) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
        }
      } catch {
        // ignore storage failures
      }
      if (!alreadyReloaded) {
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
    }

    throw lastError;
  });
}

export default lazyRetry;
